import React from "react";
import ReactDOM from "react-dom/client";

// Import router first so we can pass it to Sentry
import { router } from "./router";

// Initialize Sentry with TanStack Router integration
import { initSentry } from "./sentry";
initSentry(router);

import { ApolloProvider } from "@apollo/client";
import Intercom from "@intercom/messenger-js-sdk";
import { RouterProvider } from "@tanstack/react-router";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Ban } from "lucide-react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";

import "@fontsource-variable/inter";

import { client } from "@/apis/gql/apolloClient";
import { GlobalContextProvider } from "@/global-state/globalStateProvider";
import i18n from "@/localisation/i18next";

import { SidebarProvider } from "@/components/ui/sidebar";
import { RealtimeProvider } from "@/features/realtime-api/context-provider";
import { SentryUserManagement } from "@/providers/sentry-user-management";

import { getAuthContext } from "./context/auth";

import "../styles/globals.css";
import "../localisation/i18next";
import "@vercel/examples-ui/globals.css";
import "react-json-view-lite/dist/index.css";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Initialize Intercom
Intercom({
  app_id: import.meta.env.VITE_INTERCOM_APP_ID as string,
  hide_default_launcher: true,
  language_override: "de",
});

// Initialize PostHog
const sessionIdleTimeout = import.meta.env.VITE_POSTHOG_TIMEOUT
  ? Number.parseInt(import.meta.env.VITE_POSTHOG_TIMEOUT)
  : 180;

posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
  api_host: import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com",
  integrations: {
    intercom: true,
    crispChat: false,
  },
  person_profiles: "identified_only",
  loaded: (posthog) => {
    if (import.meta.env.DEV) posthog.debug();
  },
  session_idle_timeout_seconds: sessionIdleTimeout,
});

async function main() {
  const authContext = await getAuthContext();

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <div className="min-h-screen bg-background font-sans antialiased">
        <I18nextProvider i18n={i18n}>
          <PostHogProvider client={posthog}>
            <ApolloProvider client={client}>
              <GlobalContextProvider>
                <RealtimeProvider>
                  <SidebarProvider>
                    <RouterProvider
                      router={router}
                      context={{ auth: authContext }}
                    />
                    <SpeedInsights />
                    <Toaster
                      position="top-left"
                      icons={{
                        error: <Ban size={20} />,
                      }}
                      toastOptions={{
                        style: {
                          width: "fit-content",
                          left: "32px",
                          top: "-24px",
                          padding: "8px 16px",
                        },
                      }}
                    />
                    <SentryUserManagement />
                  </SidebarProvider>
                </RealtimeProvider>
              </GlobalContextProvider>
            </ApolloProvider>
          </PostHogProvider>
        </I18nextProvider>
      </div>
    </React.StrictMode>,
  );
}

main();
